import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, Loader2, Filter, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const ACTION_COLOR = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-sky-100 text-sky-800",
  delete: "bg-rose-100 text-rose-800",
  post: "bg-violet-100 text-violet-800",
  reversal: "bg-amber-100 text-amber-800",
  dispose: "bg-slate-100 text-slate-800",
};

const ENTITY_LABEL = {
  journal: "Buchung",
  receipt: "Beleg",
  invoice: "Rechnung",
  bill: "Lieferantenrechnung",
  asset: "Anlage",
  payroll_run: "Lohnlauf",
  company: "Firma",
};

export default function AuditLog() {
  const cid = localStorage.getItem("demoCompanyId");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ entity_type: "", action: "" });
  const [detail, setDetail] = useState(null);
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [nightly, setNightly] = useState(null);
  useEffect(() => {
    if (!cid) return;
    apiClient.get(`/companies/${cid}/audit-log/verify-status`).then((r) => setNightly(r.data)).catch(() => {});
  }, [cid]);

  const runVerify = async () => {
    setVerifying(true);
    try {
      const r = await apiClient.get(`/companies/${cid}/audit-log/verify`);
      setVerify(r.data);
      r.data.ok ? toast.success(`Kette intakt: ${r.data.checked} Einträge geprüft`) : toast.error(`Kette beschädigt ab Eintrag #${r.data.first_problem_seq}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Prüfung fehlgeschlagen"); }
    finally { setVerifying(false); }
  };

  const load = async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.action) params.action = filters.action;
      const r = await apiClient.get(`/companies/${cid}/audit-log`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Audit-Log nicht laden.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [cid, filters.entity_type, filters.action]);

  const showDetail = async (entryId) => {
    try {
      const r = await apiClient.get(`/companies/${cid}/audit-log/${entryId}/detail`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Detail nicht laden.");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="audit-log-page">
      <div className="flex items-start gap-3 mb-4">
        <History className="h-6 w-6 text-slate-700" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Audit-Trail (GeBüV)</h1>
          <p className="text-xs text-slate-500">Alle Änderungen an Buchungen, Belegen, Rechnungen und Anlagen — nachvollziehbar mit User, Zeit und Diff. Jeder Eintrag trägt den Hash des Vorgängers (fälschungssicheres Ledger).</p>
        </div>
        {nightly?.started_at && (
          <span className={`ml-auto self-center text-[11px] ${nightly.ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="audit-nightly-status">
            Letzte automatische Prüfung {new Date(nightly.started_at).toLocaleString("de-CH")}: {nightly.ok ? "intakt" : `beschädigt ab #${nightly.first_problem_seq}`}
          </span>
        )}
        <Button type="button" size="sm" variant="outline" onClick={runVerify} disabled={verifying} className={`rounded-full ${nightly?.started_at ? "" : "ml-auto"}`} data-testid="audit-verify-chain">
          {verifying ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />} Kette prüfen
        </Button>
      </div>

      {verify && (
        <div className={`mb-3 rounded-md border px-3 py-2 text-xs flex items-start gap-2 ${verify.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`} data-testid="audit-verify-result">
          {verify.ok ? <ShieldCheck className="h-4 w-4 shrink-0" /> : <ShieldAlert className="h-4 w-4 shrink-0" />}
          <div>
            <div className="font-medium">
              {verify.ok ? "Hash-Kette intakt" : `Hash-Kette beschädigt ab Eintrag #${verify.first_problem_seq}`} · {verify.checked} Einträge geprüft · Kopf #{verify.head_seq} <code className="font-mono">{verify.head_hash?.slice(0, 16)}…</code>
            </div>
            {!verify.ok && (verify.problems || []).slice(0, 5).map((pr, i) => <div key={i}>• {pr.text}</div>)}
            {verify.unchained_legacy > 0 && <div>• {verify.unchained_legacy} Alt-Einträge noch nicht verkettet</div>}
          </div>
        </div>
      )}

      <Card className="p-3 mb-3 border border-slate-200">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1 text-xs text-slate-600"><Filter className="h-3 w-3" /> Filter:</div>
          <select value={filters.entity_type} onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value }))}
            className="text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="audit-entity-filter">
            <option value="">Alle Entitäten</option>
            {Object.entries(ENTITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.action} onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
            className="text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="audit-action-filter">
            <option value="">Alle Aktionen</option>
            {Object.keys(ACTION_COLOR).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <Button type="button" size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="audit-refresh">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Aktualisieren"}
          </Button>
          <span className="text-[11px] text-slate-500 ml-auto">{data.total} Einträge</span>
        </div>
      </Card>

      <Card className="border border-slate-200 overflow-hidden">
        {data.items.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500" data-testid="audit-empty">
            Noch keine Audit-Einträge. Sobald Belege gebucht oder Buchungen geändert werden, erscheinen sie hier.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] text-slate-600 uppercase">
              <tr>
                <th className="text-left p-2">#</th>
                <th className="text-left p-2">Zeit</th>
                <th className="text-left p-2">Benutzer</th>
                <th className="text-left p-2">Entität</th>
                <th className="text-left p-2">Aktion</th>
                <th className="text-left p-2">Felder geändert</th>
                <th className="text-right p-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e._id} className="border-t border-slate-100" data-testid={`audit-row-${e._id}`}>
                  <td className="p-2 text-[11px] text-slate-500 whitespace-nowrap font-mono" title={e.hash ? `sha256 ${e.hash}` : ""}>{e.seq ?? "—"}<span className="text-slate-400"> {e.hash?.slice(0, 8)}</span></td>
                  <td className="p-2 text-[11px] text-slate-600 whitespace-nowrap">{new Date(e.timestamp).toLocaleString("de-CH")}</td>
                  <td className="p-2 text-[12px]">{e.user_email}</td>
                  <td className="p-2 text-[12px]">{ENTITY_LABEL[e.entity_type] || e.entity_type} <code className="font-mono text-[10px] text-slate-500">{e.entity_id?.slice(0, 8)}…</code></td>
                  <td className="p-2"><Badge className={ACTION_COLOR[e.action] || "bg-slate-100 text-slate-700"}>{e.action}</Badge></td>
                  <td className="p-2 text-[11px] text-slate-700">
                    {Object.keys(e.diff || {}).length > 0 ? Object.keys(e.diff).slice(0,4).join(", ") + (Object.keys(e.diff).length > 4 ? "…" : "") : "—"}
                  </td>
                  <td className="p-2 text-right">
                    <Button type="button" size="sm" variant="outline" className="rounded-full text-[11px]" onClick={() => showDetail(e._id)} data-testid={`audit-detail-${e._id}`}>
                      Detail
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setDetail(null)} data-testid="audit-detail-modal">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-auto p-4 m-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Audit-Eintrag · {detail.action}</h2>
              <Button type="button" size="sm" variant="outline" onClick={() => setDetail(null)} className="rounded-full">Schliessen</Button>
            </div>
            <pre className="bg-slate-50 p-3 rounded text-[11px] overflow-auto" data-testid="audit-detail-json">
{JSON.stringify(detail, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
